import { useId } from "react"
import { EditIcon } from "lucide-react"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import {
  getAvatarName,
  getAvatarBackgroundColor,
  isDefaultAvatar,
} from "@/lib/utils"
import { getProviderBrand } from "@/lib/brand"
import { useToast } from "@/hooks"

interface AvatarInputProps {
  className?: string
  url: string
  name: string
  subTitle?: string
  size?: string
  disabled?: boolean
  fallback?: React.ReactNode
  onChange?: (file: File[]) => void
}

const AvatarInput = ({
  className,
  url,
  name,
  subTitle,
  size = "h-48 w-48",
  disabled = false,
  fallback,
  onChange,
}: AvatarInputProps) => {
  const inputId = useId()
  const { alertToast } = useToast()

  const { icon: IconComponent } = getProviderBrand()
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) {
      alertToast({
        title: "Failed to update avatar",
        description: "No file selected",
      })
      return
    }

    try {
      onChange?.(Array.from(e.target.files))
    } catch (error) {
      console.error("Failed to update avatar:", error)
      e.target.value = ""
      alertToast({
        title: "Update Error",
        description: "Failed to update avatar",
      })
    }
  }

  return (
    <div className={className}>
      <div className={cn("relative", size)}>
        {isDefaultAvatar(url) ? (
          <IconComponent className={size} />
        ) : (
          <Avatar className={size}>
            <AvatarImage src={url} alt={name} className="object-cover" />
            <AvatarFallback
              style={
                fallback
                  ? undefined
                  : {
                      backgroundColor: getAvatarBackgroundColor(subTitle ?? ""),
                    }
              }
            >
              {fallback ?? getAvatarName(name)}
            </AvatarFallback>
          </Avatar>
        )}

        {!disabled && (
          <div
            className="absolute top-0 left-0 w-full h-full opacity-0 hover:opacity-70 cursor-pointer"
            onClick={() => document.getElementById(inputId)?.click()}
          >
            <div className="flex justify-center items-center h-full w-full bg-white rounded-full">
              <EditIcon className="h-[30%] w-[30%] text-black opacity-70" />
            </div>
          </div>
        )}
      </div>

      {/* hidden input for file upload */}
      <Input
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}

export default AvatarInput
